/** Icons ported from the prototype. Rendered as JSX, never as HTML strings. */

type IconProps = { className?: string };

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
} as const;

export function CheckIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={2.2} {...stroke} {...props} aria-hidden="true">
      <path d="M4 12.5l5.2 5.2L20 7" />
    </svg>
  );
}

/*
 * The four tab icons are drawn to fill their 24x24 box: ink runs from about 1
 * to about 23 on both axes, stroke width included.
 *
 * They used to sit inside a 24 box with ink spanning roughly 2.5 to 21.5. At a
 * 26px rendered size that left ~2.7px of empty box below the glyph, which did
 * two things at once — the icon read smaller than the size token claimed, and
 * the dead space stacked on top of the icon-to-label gap, so the label looked
 * detached from an icon that looked too small. The box is the layout; the ink
 * has to fill it or the tokens are describing something the eye never sees.
 */

export function UpNextIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={1.8} {...stroke} {...props} aria-hidden="true">
      <rect x="1.9" y="3" width="20.2" height="15.5" rx="2.8" />
      <path d="M7.6 21.5h8.8" />
    </svg>
  );
}

export function UpNextFilledIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
      <rect x="1" y="2.1" width="22" height="17.3" rx="3.6" />
      <rect x="7.2" y="20.6" width="9.6" height="2.2" rx="1.1" />
    </svg>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={1.8} {...stroke} {...props} aria-hidden="true">
      <rect x="1.9" y="4" width="20.2" height="18.1" rx="2.8" />
      <path d="M1.9 9.6h20.2M7.6 1.9v4.2M16.4 1.9v4.2" />
    </svg>
  );
}

export function CalendarFilledIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
      {/* Solid. An earlier version knocked three day marks out of the body,
          which at 26px read as arbitrary dots rather than as a calendar. */}
      <path d="M4.6 4h14.8A3.6 3.6 0 0 1 23 7.6v11a3.6 3.6 0 0 1-3.6 3.6H4.6A3.6 3.6 0 0 1 1 18.6v-11A3.6 3.6 0 0 1 4.6 4z" />
      <path
        d="M7.6 1.9v3.2M16.4 1.9v3.2"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
      />
    </svg>
  );
}

export function LibraryIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={1.8} {...stroke} {...props} aria-hidden="true">
      <path d="M3.4 2.6h3.8v18.8H3.4zM10.1 2.6h3.8v18.8h-3.8z" />
      <path d="M17 3.4l3.6.95-4.5 18.2-3.6-.95z" />
    </svg>
  );
}

export function LibraryFilledIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
      <path d="M3.4 1.7h3.8a.9.9 0 0 1 .9.9v18.8a.9.9 0 0 1-.9.9H3.4a.9.9 0 0 1-.9-.9V2.6a.9.9 0 0 1 .9-.9zm6.7 0h3.8a.9.9 0 0 1 .9.9v18.8a.9.9 0 0 1-.9.9h-3.8a.9.9 0 0 1-.9-.9V2.6a.9.9 0 0 1 .9-.9z" />
      <path d="M16.9 2.5l3.7.97a.9.9 0 0 1 .64 1.1l-4.5 18.2a.9.9 0 0 1-1.1.64l-3.7-.97a.9.9 0 0 1-.64-1.1l4.5-18.2a.9.9 0 0 1 1.1-.64z" />
    </svg>
  );
}

/*
 * Search has no filled counterpart, here or in SF Symbols: a solid disc stops
 * reading as a magnifier. Selection thickens the stroke instead.
 */
export function SearchIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={1.8} {...stroke} {...props} aria-hidden="true">
      <circle cx="10.3" cy="10.3" r="8.4" />
      <path d="M16.3 16.3l5.8 5.8" />
    </svg>
  );
}

export function SearchFilledIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={2.6} {...stroke} {...props} aria-hidden="true">
      <circle cx="10.3" cy="10.3" r="8.4" />
      <path d="M16.3 16.3l5.5 5.5" />
    </svg>
  );
}

/*
 * A cog, generated rather than hand-written so the eight teeth are actually
 * even. The first version was a circle with eight detached strokes radiating
 * from it, which is the recipe for a sun — teeth have to meet the body.
 */
const COG =
  "M19.81 10.29 L22.90 10.54 L22.90 13.46 L19.81 13.71 L18.74 16.31 L20.74 18.67 " +
  "L18.67 20.74 L16.31 18.74 L13.71 19.81 L13.46 22.90 L10.54 22.90 L10.29 19.81 " +
  "L7.69 18.74 L5.33 20.74 L3.26 18.67 L5.26 16.31 L4.19 13.71 L1.10 13.46 " +
  "L1.10 10.54 L4.19 10.29 L5.26 7.69 L3.26 5.33 L5.33 3.26 L7.69 5.26 " +
  "L10.29 4.19 L10.54 1.10 L13.46 1.10 L13.71 4.19 L16.31 5.26 L18.67 3.26 " +
  "L20.74 5.33 L18.74 7.69 Z M12 8.4a3.6 3.6 0 1 0 0 7.2 3.6 3.6 0 0 0 0-7.2Z";

export function GearIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props} aria-hidden="true">
      <path fillRule="evenodd" d={COG} />
    </svg>
  );
}

export function ChevronIcon(props: IconProps) {
  return (
    <svg viewBox="0 0 24 24" strokeWidth={2} {...stroke} {...props} aria-hidden="true">
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}
